/*
 * A copy of the dispatch console's module of the same name, kept byte-for-byte except for one
 * clause of prose that named the other console.
 *
 * Nothing in it is design content: it maps the error categories `packages/api-client` reports onto
 * the six states the design commission names, which is a property of the platform rather than of
 * any one console. It is a copy rather than a shared package for the reason recorded in
 * `docs/adr/0021-two-consoles-and-no-shared-shell.md`.
 */
import { ApiError, TransportError } from "@tdelivr/api-client";

/**
 * The six states every screen in this console has to render.
 *
 * The design commission names them — loading, empty, error, forbidden, stale,
 * conflict — and says the last two are the ones normally skipped and the ones
 * this product needs most. It is right, and the reason is that this platform
 * produces both constantly:
 *
 *   - **stale** because a console watches things that move. A driver
 *     loses signal in a car park and their marker is still on the map,
 *     confidently, in the wrong place. Showing it as current is worse than
 *     showing nothing, because an operator will dispatch against it.
 *   - **conflict** because every command here carries an idempotency key and an
 *     expected version, and two dispatchers working the same queue is normal
 *     rather than exceptional. A 409 is not an error, it is news: somebody else
 *     got there first, and the screen has to say who and what changed.
 *
 * The mapping is driven by the error category the services now return, not by
 * status codes. That is the whole reason the categories were worth fixing: a
 * screen asking "is this retryable" gets an answer from the service rather than
 * from a lookup table maintained by whoever wrote the screen.
 */

export type ViewState =
  | { readonly kind: "loading" }
  | { readonly kind: "ready"; readonly stale: false }
  /** Shown, but known to be behind. Carries how far behind, because a second is not a minute. */
  | { readonly kind: "ready"; readonly stale: true; readonly ageMs: number }
  | { readonly kind: "empty"; readonly because: "no_data" | "filtered" }
  | { readonly kind: "forbidden"; readonly missingScopes: readonly string[] }
  | { readonly kind: "conflict"; readonly code: string; readonly message: string }
  | {
      readonly kind: "error";
      readonly code: string;
      readonly message: string;
      /** Whether offering a retry button is honest. */
      readonly retryable: boolean;
      /** True when the request never reached a service at all. */
      readonly offline: boolean;
    };

/**
 * How old a live reading may be before the screen stops claiming it is current.
 *
 * Six seconds because driver location is published on a short interval and a
 * missed publish is ordinary; two missed publishes is a signal. The number is
 * a product decision and belongs where a product person can find it, not
 * buried in a component.
 */
export const STALE_AFTER_MS = 6_000;

export function stateForList<T>(
  items: readonly T[] | undefined,
  options: {
    readonly filtered?: boolean;
    readonly observedAt?: number;
    readonly now?: number;
  } = {},
): ViewState {
  if (items === undefined) return { kind: "loading" };
  if (items.length === 0) {
    // An empty list and an empty *filtered* list are different screens. One
    // says there is nothing happening; the other says your filter hid it, and
    // offers to clear it. Collapsing them is how an operator concludes the
    // system is broken when they have simply filtered themselves out.
    return { kind: "empty", because: options.filtered === true ? "filtered" : "no_data" };
  }

  const observedAt = options.observedAt;
  if (observedAt !== undefined) {
    const age = (options.now ?? Date.now()) - observedAt;
    if (age >= STALE_AFTER_MS) return { kind: "ready", stale: true, ageMs: age };
  }
  return { kind: "ready", stale: false };
}

/**
 * Turn whatever went wrong into the state a screen can render.
 *
 * Deliberately exhaustive over the platform's eight categories rather than a
 * default case, so adding a category to the runtime fails the build here
 * instead of silently rendering everything after it as a generic error.
 */
export function stateForError(error: unknown, missingScopes: readonly string[] = []): ViewState {
  if (error instanceof TransportError) {
    // Never reached a service, so there is no category and none to invent. A
    // console in a depot loses its network regularly and the screen should say
    // that rather than blame the platform.
    return {
      kind: "error",
      code: "transport_failed",
      message: "The console could not reach the platform",
      retryable: true,
      offline: true,
    };
  }

  if (!(error instanceof ApiError)) {
    return {
      kind: "error",
      code: "unexpected_error",
      message: error instanceof Error ? error.message : "Something went wrong",
      retryable: false,
      offline: false,
    };
  }

  switch (error.category) {
    case "authorization":
    case "authentication":
      return { kind: "forbidden", missingScopes };
    case "conflict":
      // Not an error state. Somebody else changed this first, and the screen's
      // job is to say so and offer the current version, not to apologise.
      return { kind: "conflict", code: error.code, message: error.message };
    case "not_found":
      return { kind: "empty", because: "no_data" };
    case "validation":
    case "rate_limit":
    case "dependency":
    case "internal":
      return {
        kind: "error",
        code: error.code,
        message: error.message,
        retryable: error.retryable,
        offline: false,
      };
  }
}

/**
 * What to put on a retry control, if anything.
 *
 * A retry button on a state that can never succeed is the single most common
 * way a console teaches its operators to mash a button during an incident.
 */
export function retryLabel(state: ViewState): string | undefined {
  if (state.kind === "error" && state.retryable) return state.offline ? "Reconnect" : "Try again";
  if (state.kind === "ready" && state.stale) return "Refresh";
  return undefined;
}
